import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ScanSearch,
  BrainCircuit,
  ListChecks,
  FileDiff,
  ShieldCheck,
  Lock,
  Layers,
  AlertTriangle,
  Settings2,
  FlaskConical,
  GitBranch,
  Boxes,
  Rocket,
  User,
  Briefcase,
  Building2,
  Check,
  ChevronDown,
  Sparkles,
  Terminal,
} from "lucide-react";
import { useState, useEffect } from "react";
import { BACKEND_URL } from "../lib/backend";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
};

const heading = { fontFamily: "Manrope, sans-serif", letterSpacing: "-0.02em" };

const Pill = ({ children, testId }) => (
  <span
    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/60 border border-slate-700 text-slate-200 text-sm font-medium backdrop-blur"
    data-testid={testId}
  >
    <Check className="w-3.5 h-3.5 text-teal-400" />
    {children}
  </span>
);

const Badge = ({ children, testId }) => (
  <span
    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-500/10 border border-teal-400/20 text-teal-300 text-xs font-semibold tracking-widest uppercase"
    data-testid={testId}
  >
    <Sparkles className="w-3.5 h-3.5" />
    {children}
  </span>
);

const PrimaryCta = ({ children, onClick, testId, large }) => (
  <button
    onClick={onClick}
    className={`group inline-flex items-center gap-2 ${large ? "px-7 py-4" : "px-5 py-2.5 text-sm"} bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold rounded-full transition-all shadow-[0_10px_40px_-10px_rgba(20,184,166,0.6)] hover:shadow-[0_20px_50px_-10px_rgba(20,184,166,0.8)] hover:-translate-y-0.5`}
    data-testid={testId}
  >
    {children}
    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
  </button>
);

// ============ HEADER ============

const AuditHeader = () => (
  <header className="sticky top-0 z-40 border-b border-slate-800 bg-slate-950/80 backdrop-blur-lg">
    <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
      <Link to="/developer-audit" className="flex items-center gap-3" data-testid="audit-logo-link">
        <img src="/nephrix-logo.png" alt="Nephrix" className="h-10 w-auto" />
        <span className="text-slate-100 font-semibold text-lg tracking-tight hidden sm:flex items-baseline gap-2" style={heading}>
          Nephrix
          <span className="text-teal-300 text-xs font-bold tracking-widest uppercase">Developer</span>
        </span>
      </Link>
      <nav className="hidden md:flex items-center gap-8">
        <button onClick={() => scrollToId("how-it-works")} className="text-sm text-slate-300 hover:text-teal-300 transition-colors" data-testid="audit-nav-how">
          How It Works
        </button>
        <button onClick={() => scrollToId("what-you-get")} className="text-sm text-slate-300 hover:text-teal-300 transition-colors" data-testid="audit-nav-what">
          What You Get
        </button>
        <button onClick={() => scrollToId("pricing")} className="text-sm text-slate-300 hover:text-teal-300 transition-colors" data-testid="audit-nav-pricing">
          Pricing
        </button>
        <button onClick={() => scrollToId("faq")} className="text-sm text-slate-300 hover:text-teal-300 transition-colors" data-testid="audit-nav-faq">
          FAQ
        </button>
      </nav>
      <button
        onClick={() => scrollToId("pricing")}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold text-sm transition-colors"
        data-testid="audit-header-cta"
      >
        Audit My Project
      </button>
    </div>
  </header>
);

// ============ HERO ============

const reportLines = [
  { sev: "HIGH", color: "text-red-400 border-red-400/30 bg-red-400/10", text: "Duplicated auth logic across 4 modules" },
  { sev: "HIGH", color: "text-red-400 border-red-400/30 bg-red-400/10", text: "No error boundaries on async data flows" },
  { sev: "MED", color: "text-amber-400 border-amber-400/30 bg-amber-400/10", text: "Env config hardcoded in 7 files" },
  { sev: "MED", color: "text-amber-400 border-amber-400/30 bg-amber-400/10", text: "0% test coverage on payment paths" },
  { sev: "LOW", color: "text-teal-300 border-teal-400/30 bg-teal-400/10", text: "Unused dependencies inflating bundle" },
];

const AuditReportMock = () => (
  <div className="relative rounded-2xl border border-slate-800 bg-slate-900/70 backdrop-blur overflow-hidden shadow-[0_30px_80px_-30px_rgba(2,6,23,0.9)]" data-testid="audit-report-mock">
    <div className="flex items-center gap-2 px-5 py-3.5 border-b border-slate-800 bg-slate-950/60">
      <span className="w-3 h-3 rounded-full bg-slate-700" />
      <span className="w-3 h-3 rounded-full bg-slate-700" />
      <span className="w-3 h-3 rounded-full bg-slate-700" />
      <span className="ml-3 flex items-center gap-2 text-xs text-slate-400 font-mono">
        <Terminal className="w-3.5 h-3.5 text-teal-400" />
        nephrix audit — remediation plan
      </span>
    </div>
    <div className="p-5 space-y-2.5 font-mono text-[13px]">
      {reportLines.map((l, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: -12 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.15 + i * 0.12, duration: 0.4 }}
          className="flex items-center gap-3 rounded-lg border border-slate-800/80 bg-slate-950/50 px-4 py-3"
        >
          <span className={`shrink-0 px-2 py-0.5 rounded-full border text-[10px] font-bold tracking-wider ${l.color}`}>{l.sev}</span>
          <span className="text-slate-300 truncate">{l.text}</span>
        </motion.div>
      ))}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.9 }}
        className="flex items-center justify-between rounded-lg border border-teal-400/20 bg-teal-500/10 px-4 py-3"
      >
        <span className="text-teal-300">→ 12 prioritized actions prepared</span>
        <span className="text-teal-300/60 text-xs">read-only preview</span>
      </motion.div>
    </div>
  </div>
);

const Hero = () => (
  <section className="relative overflow-hidden pt-16 pb-20 md:pt-24 md:pb-28">
    <div
      className="pointer-events-none absolute inset-0 opacity-[0.35]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(51,65,85,0.25) 1px, transparent 1px), linear-gradient(90deg, rgba(51,65,85,0.25) 1px, transparent 1px)",
        backgroundSize: "56px 56px",
        maskImage: "radial-gradient(ellipse 80% 60% at 50% 0%, black 40%, transparent 100%)",
      }}
    />
    <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-teal-500/10 blur-[120px]" />
    <div className="relative max-w-6xl mx-auto px-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <motion.div variants={fadeUp} initial="hidden" animate="visible">
          <Badge testId="hero-badge">AI Codebase Audit</Badge>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-50 tracking-tight leading-[1.05]" style={heading} data-testid="hero-title">
            Your AI-built app works.
            <br />
            <span className="text-teal-300">Until it doesn't.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed max-w-xl" data-testid="hero-subtitle">
            Built with Cursor, Claude, Lovable, Bolt, Replit or AI-assisted code? Nephrix Developer analyzes your real
            codebase, identifies technical and structural problems, prioritizes what matters and prepares a clear
            remediation plan before you waste another week debugging blindly.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <PrimaryCta large onClick={() => scrollToId("pricing")} testId="hero-primary-cta">
              Audit My Project
            </PrimaryCta>
            <button
              onClick={() => scrollToId("how-it-works")}
              className="inline-flex items-center gap-2 px-7 py-4 bg-slate-800/60 hover:bg-slate-800 border border-slate-700 text-slate-100 font-semibold rounded-full transition-colors backdrop-blur"
              data-testid="hero-secondary-cta"
            >
              See How It Works
            </button>
          </div>
          <p className="mt-5 text-sm text-teal-300/90 font-medium" data-testid="hero-trust-line">
            Launch offer · First 10 audit slots
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Pill testId="hero-pill-analysis">Real codebase analysis</Pill>
            <Pill testId="hero-pill-remediation">Prioritized remediation</Pill>
            <Pill testId="hero-pill-previews">Read-only patch previews</Pill>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}>
          <AuditReportMock />
        </motion.div>
      </div>
      <motion.p
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="mt-16 md:mt-20 text-center text-xl md:text-2xl font-semibold text-slate-200"
        style={heading}
        data-testid="hero-positioning"
      >
        Not another AI chat. <span className="text-teal-300">A technical diagnosis of your actual project.</span>
      </motion.p>
    </div>
  </section>
);

// ============ PROBLEM ============

const problems = [
  { icon: Layers, label: "Fragile architecture" },
  { icon: AlertTriangle, label: "Hidden technical debt" },
  { icon: Settings2, label: "Configuration issues" },
  { icon: FlaskConical, label: "Missing verification" },
  { icon: ListChecks, label: "Unclear priorities" },
  { icon: GitBranch, label: "AI-generated complexity" },
];

const ProblemSection = () => (
  <section className="relative py-24 md:py-32 bg-slate-900/30 border-y border-slate-800/60">
    <div className="max-w-6xl mx-auto px-6">
      <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="max-w-2xl">
        <h2 className="text-3xl md:text-5xl font-bold text-slate-50 tracking-tight leading-[1.08]" style={heading} data-testid="problem-title">
          You built fast.
          <br />
          <span className="text-teal-300">Now you need to know what you actually built.</span>
        </h2>
        <p className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed">
          AI can help you create an MVP in days. But as the project grows, bugs, duplicated logic, fragile architecture,
          unclear dependencies, missing tests, configuration problems and technical debt can start accumulating faster
          than you can understand them.
        </p>
        <p className="mt-4 text-base md:text-lg text-slate-100 font-semibold">
          Nephrix gives your project a second technical opinion.
        </p>
      </motion.div>
      <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-4">
        {problems.map((p, i) => (
          <motion.div
            key={p.label}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ delay: i * 0.06 }}
            className="group rounded-2xl border border-slate-800 bg-slate-950/60 p-6 hover:border-teal-400/30 hover:bg-slate-900/60 transition-colors"
            data-testid={`problem-card-${i}`}
          >
            <div className="w-11 h-11 rounded-xl bg-teal-500/10 border border-teal-400/20 flex items-center justify-center group-hover:bg-teal-500/20 transition-colors">
              <p.icon className="w-5 h-5 text-teal-300" />
            </div>
            <p className="mt-4 text-slate-100 font-semibold text-sm md:text-base">{p.label}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// ============ HOW IT WORKS ============

const steps = [
  {
    num: "01",
    icon: ScanSearch,
    title: "Project Inspection",
    text: "Nephrix examines your project structure, configuration, dependencies, code health, tests and available development checks.",
  },
  {
    num: "02",
    icon: BrainCircuit,
    title: "AI Technical Analysis",
    text: "The Developer Agent analyzes the findings and separates important problems from background noise.",
  },
  {
    num: "03",
    icon: ListChecks,
    title: "Remediation Planning",
    text: "You receive prioritized actions explaining what should be addressed first and why.",
  },
  {
    num: "04",
    icon: FileDiff,
    title: "Safe Patch Proposals",
    text: "Where supported, Nephrix prepares a read-only preview of the proposed modification before anything touches your files.",
  },
];

const HowItWorks = () => (
  <section id="how-it-works" className="relative py-24 md:py-32">
    <div className="max-w-6xl mx-auto px-6">
      <motion.h2
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="text-3xl md:text-5xl font-bold text-slate-50 tracking-tight"
        style={heading}
        data-testid="how-title"
      >
        From codebase to <span className="text-teal-300">clear action plan.</span>
      </motion.h2>
      <div className="mt-14 grid md:grid-cols-2 gap-5">
        {steps.map((s, i) => (
          <motion.div
            key={s.num}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ delay: i * 0.08 }}
            className="relative rounded-2xl border border-slate-800 bg-slate-900/40 p-7 md:p-8 overflow-hidden hover:border-teal-400/30 transition-colors"
            data-testid={`step-card-${s.num}`}
          >
            <span className="absolute -top-3 right-5 text-[72px] font-extrabold text-slate-800/50 select-none" style={heading}>
              {s.num}
            </span>
            <div className="relative w-11 h-11 rounded-xl bg-teal-500/10 border border-teal-400/20 flex items-center justify-center">
              <s.icon className="w-5 h-5 text-teal-300" />
            </div>
            <h3 className="relative mt-5 text-lg font-bold text-slate-50" style={heading}>
              {s.title}
            </h3>
            <p className="relative mt-3 text-sm md:text-[15px] text-slate-300 leading-relaxed">{s.text}</p>
          </motion.div>
        ))}
      </div>
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="mt-8 flex items-start gap-4 rounded-2xl border border-teal-400/20 bg-teal-500/[0.07] p-6 md:p-7"
        data-testid="safety-callout"
      >
        <div className="shrink-0 w-11 h-11 rounded-xl bg-teal-500/15 border border-teal-400/25 flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-teal-300" />
        </div>
        <div>
          <p className="text-slate-50 font-bold" style={heading}>
            You stay in control.
          </p>
          <p className="mt-1 text-sm md:text-[15px] text-slate-300">
            During the audit, Nephrix does not autonomously modify your code.
          </p>
        </div>
      </motion.div>
    </div>
  </section>
);

// ============ WHO IT IS FOR ============

const audiences = [
  { icon: Rocket, title: "AI / Vibe-Coded Startups", text: "You built quickly with AI and now need to understand the technical foundation." },
  { icon: User, title: "Solo Developers", text: "Get an independent technical review without spending days reviewing your own project." },
  { icon: Briefcase, title: "Non-Technical Founders", text: "Understand the real technical state of the product someone built for you." },
  { icon: Building2, title: "Software Agencies", text: "Run an independent health check before delivering a client project." },
];

const WhoItsFor = () => (
  <section id="what-you-get" className="relative py-24 md:py-32 bg-slate-900/30 border-y border-slate-800/60">
    <div className="max-w-6xl mx-auto px-6">
      <motion.h2
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="text-3xl md:text-5xl font-bold text-slate-50 tracking-tight"
        style={heading}
        data-testid="who-title"
      >
        Built for teams that <span className="text-teal-300">moved fast.</span>
      </motion.h2>
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {audiences.map((a, i) => (
          <motion.div
            key={a.title}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ delay: i * 0.08 }}
            className="rounded-2xl border border-slate-800 bg-slate-950/60 p-7 hover:border-teal-400/30 hover:-translate-y-1 transition-all"
            data-testid={`audience-card-${i}`}
          >
            <div className="w-11 h-11 rounded-xl bg-teal-500/10 border border-teal-400/20 flex items-center justify-center">
              <a.icon className="w-5 h-5 text-teal-300" />
            </div>
            <h3 className="mt-5 text-base font-bold text-slate-50" style={heading}>
              {a.title}
            </h3>
            <p className="mt-2.5 text-sm text-slate-300 leading-relaxed">{a.text}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// ============ PRICING ============

const plans = [
  {
    package: "quick_diagnosis",
    name: "Quick Diagnosis",
    price: "€99",
    tagline: "For early-stage projects where you need fast clarity.",
    includes: ["Project health inspection", "Top technical issues", "Severity and priorities", "Recommended next actions", "Short technical report"],
    turnaround: "24–48 hours",
    cta: "Start for €99",
    highlight: false,
  },
  {
    package: "deep_audit",
    name: "Deep Audit",
    price: "€249",
    tagline: "For projects that need a proper technical review.",
    includes: [
      "Everything in Quick Diagnosis",
      "AI technical analysis",
      "Prioritized remediation plan",
      "Files potentially affected",
      "Verification recommendations",
      "Patch Proposals where supported",
      "Detailed final audit report",
    ],
    turnaround: "48 hours",
    cta: "Get My Deep Audit",
    highlight: true,
  },
  {
    package: "fix_sprint",
    name: "Fix Sprint",
    price: "From €499",
    tagline: "For founders who want help solving the problems we identify.",
    includes: ["Full Deep Audit", "Priority issue selection", "Assisted remediation", "Verification after changes", "Direct communication during the sprint"],
    turnaround: null,
    cta: "Request Fix Sprint",
    highlight: false,
  },
];

const PricingSection = () => {
  const navigate = useNavigate();
  const [loadingPkg, setLoadingPkg] = useState(null);
  const [checkoutError, setCheckoutError] = useState("");

  const handlePlanCta = async (plan) => {
    if (plan.package === "fix_sprint") {
      navigate("/developer-audit/intake?package=fix-sprint");
      return;
    }
    setCheckoutError("");
    setLoadingPkg(plan.package);
    try {
      const res = await fetch(`${BACKEND_URL}/api/audit/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ package: plan.package, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (res.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setCheckoutError("Could not start checkout. Please try again.");
    } catch (_e) {
      setCheckoutError("Could not start checkout. Please try again.");
    }
    setLoadingPkg(null);
  };

  return (
  <section id="pricing" className="relative py-24 md:py-32 overflow-hidden">
    <div className="pointer-events-none absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full bg-teal-500/[0.07] blur-[130px]" />
    <div className="relative max-w-6xl mx-auto px-6">
      <motion.h2
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="text-3xl md:text-5xl font-bold text-slate-50 tracking-tight text-center"
        style={heading}
        data-testid="pricing-title"
      >
        Choose your <span className="text-teal-300">audit.</span>
      </motion.h2>
      <div className="mt-14 grid lg:grid-cols-3 gap-6 items-stretch">
        {plans.map((p, i) => (
          <motion.div
            key={p.name}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className={`relative flex flex-col rounded-2xl p-8 ${
              p.highlight
                ? "border-2 border-teal-400/50 bg-slate-900/70 shadow-[0_30px_80px_-30px_rgba(20,184,166,0.35)] lg:-translate-y-3"
                : "border border-slate-800 bg-slate-900/40"
            }`}
            data-testid={`pricing-card-${i}`}
          >
            {p.highlight && (
              <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-teal-500 text-slate-950 text-xs font-bold tracking-widest uppercase whitespace-nowrap">
                Most Popular
              </span>
            )}
            <h3 className="text-lg font-bold text-slate-50" style={heading}>
              {p.name}
            </h3>
            <p className="mt-3 text-4xl font-extrabold text-slate-50" style={heading}>
              {p.price}
            </p>
            <p className="mt-3 text-sm text-slate-300 leading-relaxed">{p.tagline}</p>
            <ul className="mt-6 space-y-3 flex-1">
              {p.includes.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-slate-300">
                  <Check className="w-4 h-4 text-teal-400 shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
            {p.turnaround && (
              <p className="mt-6 text-xs text-slate-400">
                Turnaround: <span className="text-slate-200 font-semibold">{p.turnaround}</span>
              </p>
            )}
            <button
              onClick={() => handlePlanCta(p)}
              disabled={loadingPkg !== null}
              className={`mt-6 w-full py-3.5 rounded-full font-bold text-sm transition-all disabled:opacity-60 ${
                p.highlight
                  ? "bg-teal-500 hover:bg-teal-400 text-slate-950 shadow-[0_10px_40px_-10px_rgba(20,184,166,0.6)]"
                  : "bg-slate-800/80 hover:bg-slate-700 border border-slate-700 text-slate-100"
              }`}
              data-testid={`pricing-cta-${i}`}
            >
              {loadingPkg === p.package ? "Redirecting to checkout…" : p.cta}
            </button>
            {p.highlight && (
              <Link
                to="/developer-audit/sample-report"
                className="mt-3 text-center text-xs text-teal-300/80 hover:text-teal-300 hover:underline transition-colors"
                data-testid="view-sample-report-link"
              >
                View Sample Audit Report
              </Link>
            )}
          </motion.div>
        ))}
      </div>
      {checkoutError && (
        <p className="mt-6 text-center text-sm text-red-400" data-testid="checkout-error">
          {checkoutError}
        </p>
      )}
    </div>
  </section>
  );
};

// ============ LAUNCH OFFER ============

const LaunchOffer = () => (
  <section className="relative py-24 md:py-28">
    <div className="max-w-4xl mx-auto px-6">
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="relative overflow-hidden rounded-3xl border border-teal-400/25 bg-slate-900/60 p-10 md:p-14 text-center"
        data-testid="launch-offer"
      >
        <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[500px] h-[260px] rounded-full bg-teal-500/15 blur-[100px]" />
        <div className="relative">
          <Badge testId="launch-badge">Founding Clients</Badge>
          <h2 className="mt-6 text-3xl md:text-5xl font-bold text-slate-50 tracking-tight leading-[1.1]" style={heading}>
            The first 10 Nephrix Developer Audits are now open.
          </h2>
          <p className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl mx-auto">
            We are opening a limited number of projects while Nephrix Developer enters its first commercial phase.
            Launch clients receive introductory pricing and can directly influence what we build next.
          </p>
          <p className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-teal-500/10 border border-teal-400/25 text-teal-300 font-bold text-sm" data-testid="launch-slots">
            10 launch slots available.
          </p>
          <div className="mt-8">
            <PrimaryCta large onClick={() => scrollToId("pricing")} testId="launch-cta">
              Reserve My Audit
            </PrimaryCta>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

// ============ PRIVACY ============

const PrivacySection = () => (
  <section className="relative py-24 md:py-28 bg-slate-900/30 border-y border-slate-800/60">
    <div className="max-w-5xl mx-auto px-6">
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="grid md:grid-cols-[auto,1fr] gap-8 items-start"
        data-testid="privacy-section"
      >
        <div className="w-16 h-16 rounded-2xl bg-teal-500/10 border border-teal-400/25 flex items-center justify-center">
          <Lock className="w-7 h-7 text-teal-300" />
        </div>
        <div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-50 tracking-tight" style={heading}>
            Your code stays <span className="text-teal-300">your code.</span>
          </h2>
          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            {[
              "Your project is used only to perform the audit you requested.",
              "We do not publish your proprietary code or use it as marketing material without your permission.",
              "Repository access can be removed immediately after the audit is completed.",
            ].map((t, i) => (
              <div key={i} className="rounded-xl border border-slate-800 bg-slate-950/60 p-5">
                <ShieldCheck className="w-5 h-5 text-teal-400" />
                <p className="mt-3 text-sm text-slate-300 leading-relaxed">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

// ============ FAQ ============

const faqs = [
  {
    q: "Will Nephrix automatically change my code?",
    a: "No. The current audit workflow analyzes the project and can prepare read-only Patch Proposals. Changes are not autonomously applied during the audit.",
  },
  {
    q: "Do I need to have built my application with AI?",
    a: "No. Nephrix can audit traditionally developed projects as well, but the service is particularly useful for rapidly built AI-assisted applications.",
  },
  {
    q: "What technologies do you support?",
    a: "We confirm compatibility before accepting the project. During the launch period we deliberately limit the number and type of projects we accept.",
  },
  {
    q: "How do you receive my project?",
    a: "After ordering, we contact you with the secure options available for providing repository or project access.",
  },
  {
    q: "What happens after the audit?",
    a: "You receive the findings and recommended remediation order. You can implement the changes yourself or request a Nephrix Fix Sprint.",
  },
];

const FaqItem = ({ q, a, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left hover:bg-slate-900/60 transition-colors"
        data-testid={`faq-question-${index}`}
      >
        <span className="text-slate-100 font-semibold text-sm md:text-base" style={heading}>
          {q}
        </span>
        <ChevronDown className={`w-5 h-5 text-teal-300 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <p className="px-6 pb-5 text-sm md:text-[15px] text-slate-300 leading-relaxed" data-testid={`faq-answer-${index}`}>
          {a}
        </p>
      )}
    </div>
  );
};

const FaqSection = () => (
  <section id="faq" className="relative py-24 md:py-32">
    <div className="max-w-3xl mx-auto px-6">
      <motion.h2
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="text-3xl md:text-5xl font-bold text-slate-50 tracking-tight text-center"
        style={heading}
        data-testid="faq-title"
      >
        Questions, <span className="text-teal-300">answered.</span>
      </motion.h2>
      <div className="mt-12 space-y-4">
        {faqs.map((f, i) => (
          <FaqItem key={i} q={f.q} a={f.a} index={i} />
        ))}
      </div>
    </div>
  </section>
);

// ============ FINAL CTA ============

const FinalCta = () => (
  <section className="relative py-24 md:py-32 bg-slate-900/30 border-t border-slate-800/60 overflow-hidden">
    <div className="pointer-events-none absolute bottom-0 left-1/2 -translate-x-1/2 w-[700px] h-[300px] rounded-full bg-teal-500/10 blur-[120px]" />
    <div className="relative max-w-3xl mx-auto px-6 text-center">
      <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
        <h2 className="text-3xl md:text-5xl font-bold text-slate-50 tracking-tight leading-[1.1]" style={heading} data-testid="final-cta-title">
          Stop guessing what is wrong with your project.
        </h2>
        <p className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed">
          Get an independent technical diagnosis before adding more code to a foundation you don't fully trust.
          <br />
          <span className="text-slate-100 font-semibold">Developer Audits from €99.</span>
        </p>
        <div className="mt-8 flex justify-center">
          <PrimaryCta large onClick={() => scrollToId("pricing")} testId="final-cta-button">
            Audit My Project
          </PrimaryCta>
        </div>
        <p className="mt-4 text-xs text-slate-400">First 10 launch slots.</p>
      </motion.div>
    </div>
  </section>
);

// ============ FOOTER ============

const AuditFooter = () => (
  <footer className="border-t border-slate-800 py-12">
    <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
      <div className="flex items-center gap-3">
        <img src="/nephrix-logo.png" alt="Nephrix" className="h-9 w-auto" />
        <span className="text-slate-200 font-semibold text-sm" style={heading}>
          Nephrix Developer Audit
        </span>
      </div>
      <div className="flex items-center gap-6 text-sm text-slate-400">
        <Link to="/privacy" className="hover:text-teal-300 transition-colors" data-testid="footer-privacy">
          Privacy
        </Link>
        <a href="mailto:beta@nephrixapp.com" className="hover:text-teal-300 transition-colors" data-testid="footer-contact">
          Contact
        </a>
        <a href="https://www.nephrixapp.com" className="hover:text-teal-300 transition-colors" data-testid="footer-site">
          nephrixapp.com
        </a>
      </div>
      <p className="text-xs text-slate-500">© 2026 Nephrix. All rights reserved.</p>
    </div>
  </footer>
);

export default function DeveloperAudit() {
  const [cancelled, setCancelled] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("payment") === "cancelled") {
      window.history.replaceState({}, "", "/developer-audit");
      return true;
    }
    return false;
  });

  useEffect(() => {
    if (window.location.pathname.endsWith("/developer-audit/index.html")) {
      window.history.replaceState({}, "", "/developer-audit" + window.location.search + window.location.hash);
    }
    if (window.location.hash === "#pricing") {
      setTimeout(() => scrollToId("pricing"), 150);
    }
    const AUDIT_META = {
      'meta[name="description"]':
        "Production-readiness audits for AI-built apps. Review code, security, architecture, APIs, deployment risks and AI-generated issues before launch.",
      'meta[property="og:title"]': "Nephrix Developer Audit | AI App Production Readiness",
      'meta[property="og:description"]':
        "Find what AI missed before your users do. Code, security, architecture and deployment audits for AI-built applications.",
      'meta[property="og:url"]': "https://nephrixapp.com/developer-audit",
      'meta[property="og:image"]': "https://nephrixapp.com/og-developer-audit.jpg",
      'meta[name="twitter:title"]': "Nephrix Developer Audit | AI App Production Readiness",
      'meta[name="twitter:description"]':
        "Find what AI missed before your users do. Code, security, architecture and deployment audits for AI-built applications.",
      'meta[name="twitter:image"]': "https://nephrixapp.com/og-developer-audit.jpg",
    };
    const prevTitle = document.title;
    document.title = "Nephrix Developer Audit | AI App Production Readiness";
    const restored = [];
    for (const [selector, content] of Object.entries(AUDIT_META)) {
      const el = document.querySelector(selector);
      if (el) {
        restored.push([el, "content", el.getAttribute("content")]);
        el.setAttribute("content", content);
      }
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      restored.push([canonical, "href", canonical.getAttribute("href")]);
      canonical.setAttribute("href", "https://nephrixapp.com/developer-audit");
    }
    return () => {
      document.title = prevTitle;
      for (const [el, attr, prev] of restored) {
        if (prev !== null) el.setAttribute(attr, prev);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 antialiased" data-testid="developer-audit-page">
      <AuditHeader />
      {cancelled && (
        <div className="max-w-6xl mx-auto px-6 pt-4">
          <div
            className="flex items-center justify-between gap-4 rounded-xl border border-slate-700 bg-slate-900/70 px-4 py-3 text-sm text-slate-300"
            data-testid="payment-cancelled-banner"
          >
            <span>Payment was not completed. Your audit slot has not been reserved.</span>
            <button
              onClick={() => setCancelled(false)}
              className="text-slate-500 hover:text-slate-200 transition-colors font-bold px-2"
              aria-label="Dismiss"
              data-testid="dismiss-cancel-banner"
            >
              ✕
            </button>
          </div>
        </div>
      )}
      <main>
        <Hero />
        <ProblemSection />
        <HowItWorks />
        <WhoItsFor />
        <PricingSection />
        <LaunchOffer />
        <PrivacySection />
        <FaqSection />
        <FinalCta />
      </main>
      <AuditFooter />
    </div>
  );
}
